import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { makeStyles, Typography } from '@material-ui/core'
import { Highlighted } from '../../root/components/common/highlighted'
import { TableVirtualized } from '../../root/components/common/table-virtualized/table'
import CustomButton from '../../root/components/common/button'
import CustomCheckbox from '../../root/components/common/checkbox'
import { HOT_SWAP_STATUS } from '../constants/constants'
import HotSwapShowDeviceLabel from './how-swap-show-device-label'

export default function HotSwapDashboardTable({
    hotSwapLocations,
    search,
    handleHotSwapStart,
    selectedLocations,
    setSelectedLocations,
    sorting,
    onSort,
    isLoading
}) {
    const classes = useStyles()
    const { locale } = useSelector(state => state.main)
    const history = useHistory()

    const handleSelect = (isChecked, location) => {
        if (isChecked) {
            setSelectedLocations(prevLocations => [...prevLocations, location])
        } else {
            setSelectedLocations(prevLocations =>
                prevLocations.filter(loc => loc !== location)
            )
        }
    }

    const columns = [
        {
            key: 'checkbox',
            label: '',
            sortable: false,
            width: 1.5,
            minWidth: 40,
            maxWidth: 40,
            alignCenter: true,
            headerClasses: classes.cbHotSwap,
            render: ({ rowData }) => {
                return rowData.status === HOT_SWAP_STATUS.open ? (
                    <div className={classes.cbHotSwap}>
                        <CustomCheckbox
                            onClick={e =>
                                handleSelect(e.target.checked, rowData)
                            }
                            className={classes.cbHotSwapChecker}
                        />
                    </div>
                ) : null
            }
        },
        {
            key: 'location',
            label: locale.locations.location,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData }) => {
                return (
                    <Typography
                        className={classes.locationCellDiv}
                        variant="inherit"
                        noWrap
                    >
                        <div className={classes.highlightItemBold}>
                            <Highlighted
                                text={rowData.name}
                                highlight={search}
                            />
                        </div>
                        <Highlighted
                            text={rowData.customer_name}
                            highlight={search}
                        />
                    </Typography>
                )
            }
        },
        {
            key: 'status',
            label: locale.locations.status,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData }) => {
                return (
                    <Typography noWrap variant="inherit">
                        <div
                            className={`${classes.labelWrapper} ${
                                rowData.status === HOT_SWAP_STATUS.open
                                    ? classes.statusOpen
                                    : rowData.status === HOT_SWAP_STATUS.shipped
                                    ? classes.statusShipped
                                    : classes.statusFinished
                            }`}
                        >
                            <Highlighted
                                text={rowData.status}
                                highlight={search}
                            />
                        </div>
                    </Typography>
                )
            }
        },
        {
            key: 'devices',
            label: locale.devices.devices,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData }) => {
                return (
                    <div className={classes.labelWrapper}>
                        <HotSwapShowDeviceLabel
                            deviceQuantity={rowData.device_count}
                            search={search}
                        />
                    </div>
                )
            }
        },
        {
            key: 'actions',
            label: locale.devices.actions,
            sortable: false,
            width: 1.5,
            minWidth: 140,
            alignCenter: true,
            render: ({ rowData }) => {
                return selectedLocations.length < 2 ? (
                    rowData.status === HOT_SWAP_STATUS.open ? (
                        <CustomButton
                            reverted
                            onClick={() => {
                                handleHotSwapStart(rowData)
                            }}
                        >
                            {locale.hotSwap.startHotSwap}
                        </CustomButton>
                    ) : (
                        <CustomButton
                            onClick={() =>
                                history.push(`/hot-swap/${rowData.hotswapId}`)
                            }
                        >
                            {locale.devices.details}
                        </CustomButton>
                    )
                ) : null
            }
        }
    ]

    return (
        <TableVirtualized
            data={hotSwapLocations}
            columns={columns}
            rowHeight={70}
            sorting={sorting}
            onSort={onSort}
            isLoading={isLoading}
        />
    )
}

const useStyles = makeStyles(theme => ({
    highlightItemBold: {
        color: theme.textPrimary,
        fontWeight: 700
    },
    cbHotSwap: {
        display: 'flex',
        justifyContent: 'center'
    },
    cbHotSwapChecker: {
        margin: '0'
    },
    locationCellDiv: {
        display: 'flex',
        flexDirection: 'column'
    },
    labelWrapper: {
        borderRadius: theme.borderRadius,
        fontWeight: 500,
        fontSize: '12px',
        padding: '3px 6px',
        backgroundColor: theme.grayLight
    },
    statusOpen: {
        color: theme.gray,
        backgroundColor: theme.grayLight
    },
    statusShipped: {
        color: theme.blue,
        backgroundColor: theme.blueLight
    },
    statusFinished: {
        color: theme.green,
        backgroundColor: theme.greenLight
    }
}))
